import { useCallback, useEffect, useState } from 'react';

/**
 * Lets staff drag a fixed-position floating button (help speed dial, Staff Tools Assist launcher)
 * somewhere else on screen. The spot is saved per browser as distances from the bottom-right
 * corner, so the button keeps its relation to that corner when the window resizes, and is pulled
 * back on screen whenever it no longer fits. #36893
 */

const STORAGE_PREFIX = 'floatingButtonPosition:';
const CHANGE_EVENT = 'floating-button-positions-change';
// How far the pointer travels before a press becomes a drag, so a plain click still works.
const DRAG_THRESHOLD_PX = 5;
const POSITION_FIELDS = ['right', 'bottom', 'width', 'height'];

// Fixed elements are laid out against the viewport without its scrollbar, unlike window.innerWidth.
const clampToViewport = (position) => {
  const { clientWidth, clientHeight } = document.documentElement;
  return {
    ...position,
    right: Math.max(0, Math.min(position.right, clientWidth - position.width)),
    bottom: Math.max(0, Math.min(position.bottom, clientHeight - position.height))
  };
};

const readSavedPosition = (storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    return POSITION_FIELDS.every((field) => Number.isFinite(saved?.[field])) ? clampToViewport(saved) : null;
  } catch {
    return null;
  }
};

const hasSavedPositions = () => {
  try {
    return Object.keys(localStorage).some((key) => key.startsWith(STORAGE_PREFIX));
  } catch {
    return false;
  }
};

export const resetFloatingPositions = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage is unavailable, so nothing was saved; the in-memory positions still reset below.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const useHasMovedFloatingButtons = () => {
  const [hasMoved, setHasMoved] = useState(hasSavedPositions);
  useEffect(() => {
    const update = () => setHasMoved(hasSavedPositions());
    window.addEventListener(CHANGE_EVENT, update);
    return () => window.removeEventListener(CHANGE_EVENT, update);
  }, []);
  return hasMoved;
};

/**
 * Returns the saved `{ right, bottom }` offset in px (null until the button is dragged) and a
 * pointerdown handler. `box` is the fixed element being moved; it is kept fully on screen.
 */
export const useFloatingPosition = (name) => {
  const storageKey = `${STORAGE_PREFIX}${name}`;
  const [position, setPosition] = useState(() => readSavedPosition(storageKey));

  useEffect(() => {
    // Clamp from the saved spot, not the current one, so shrinking the window and growing it back
    // returns the button to where it was dropped.
    const handleResize = () =>
      setPosition((current) => readSavedPosition(storageKey) ?? (current && clampToViewport(current)));
    const handleChange = () => setPosition(readSavedPosition(storageKey));
    window.addEventListener('resize', handleResize);
    window.addEventListener(CHANGE_EVENT, handleChange);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(CHANGE_EVENT, handleChange);
    };
  }, [storageKey]);

  const startDrag = useCallback(
    (event, box) => {
      if (event.button !== 0 || !box) {
        return;
      }
      const { clientWidth, clientHeight } = document.documentElement;
      const boxRect = box.getBoundingClientRect();
      const origin = {
        x: event.clientX,
        y: event.clientY,
        right: clientWidth - boxRect.right,
        bottom: clientHeight - boxRect.bottom
      };
      let dragged = null;

      const handleMove = (moveEvent) => {
        const dx = moveEvent.clientX - origin.x;
        const dy = moveEvent.clientY - origin.y;
        if (!dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
          return;
        }
        dragged = clampToViewport({
          right: origin.right - dx,
          bottom: origin.bottom - dy,
          width: boxRect.width,
          height: boxRect.height
        });
        setPosition(dragged);
      };

      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
        if (!dragged) {
          return;
        }
        try {
          localStorage.setItem(storageKey, JSON.stringify(dragged));
          window.dispatchEvent(new Event(CHANGE_EVENT));
        } catch {
          // Storage is unavailable; the button stays where it was dropped until the page reloads.
        }
        // Releasing the drag would otherwise click (open) the button it was dragged by.
        const swallowClick = (clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
        };
        window.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }));
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [storageKey]
  );

  return { position, startDrag };
};
